/**
 * MealSearch component
 * Search input with debouncing for meal filtering
 */

import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

interface MealSearchProps {
  value: string;
  onChange: (value: string) => void;
  debounceMs?: number;
}

/**
 * MealSearch component
 *
 * Features:
 * - Debounced input to reduce API calls
 * - Clear button when has value
 * - Search icon
 * - Accessible with labels
 *
 * @example
 * ```tsx
 * const [search, setSearch] = useState('');
 * <MealSearch value={search} onChange={setSearch} debounceMs={300} />
 * ```
 */
export function MealSearch({
  value,
  onChange,
  debounceMs = 300,
}: MealSearchProps) {
  const { t } = useTranslation();
  const [localValue, setLocalValue] = useState(value);
  const lastReportedValue = useRef(value);

  // Debounce effect - only call onChange when localValue actually changed
  useEffect(() => {
    // Skip if value hasn't changed from what we last reported
    if (localValue === lastReportedValue.current) {
      return;
    }

    const timer = setTimeout(() => {
      lastReportedValue.current = localValue;
      onChange(localValue);
    }, debounceMs);

    return () => clearTimeout(timer);
  }, [localValue, debounceMs, onChange]);

  // Sync with external value changes
  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  const handleClear = () => {
    setLocalValue('');
    onChange('');
  };

  return (
    <div className="relative">
      <label htmlFor="meal-search" className="sr-only">
        {t('meals.searchMeals')}
      </label>
      <div className="relative">
        <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
          <svg
            className="h-5 w-5 text-gray-400"
            viewBox="0 0 20 20"
            fill="currentColor"
            aria-hidden="true"
          >
            <path
              fillRule="evenodd"
              d="M9 3.5a5.5 5.5 0 100 11 5.5 5.5 0 000-11zM2 9a7 7 0 1112.452 4.391l3.328 3.329a.75.75 0 11-1.06 1.06l-3.329-3.328A7 7 0 012 9z"
              clipRule="evenodd"
            />
          </svg>
        </div>
        <input
          type="search"
          id="meal-search"
          name="search"
          className="block w-full rounded-md border-0 py-2 pl-10 pr-10 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
          placeholder={t('meals.searchPlaceholder')}
          value={localValue}
          onChange={(e) => setLocalValue(e.target.value)}
        />
        {localValue && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute inset-y-0 right-0 flex items-center pr-3 hover:text-gray-700"
            aria-label={t('meals.clearSearch')}
          >
            <svg
              className="h-5 w-5 text-gray-400"
              viewBox="0 0 20 20"
              fill="currentColor"
              aria-hidden="true"
            >
              <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
}
